import { Extension } from '@tiptap/core';
import { toggleBulletListSafely, toggleOrderedListSafely } from './ordered-list-with-start';
import { toggleTaskListSafely } from './list-type-conversion';
import { indentAtCursor, outdentAtCursor } from './paragraph-indent';
import { exitEmptyListItemOnEnter } from './list-item-exit';

/**
 * Custom keyboard shortcuts extension for the rich editor.
 * Provides convenient shortcuts for common editing actions.
 *
 * Current shortcuts:
 * - Ctrl+S (Cmd+S on Mac): Save current note
 * - Ctrl+K (Cmd+K on Mac): Insert/edit web link
 * - Ctrl+Shift+L (Cmd+Shift+L on Mac): Insert note link
 * - Ctrl+Shift+T (Cmd+Shift+T on Mac): Insert table
 * - Ctrl+Shift+R (Cmd+Shift+R on Mac): Insert row below in table
 * - Ctrl+Shift+C (Cmd+Shift+C on Mac): Insert collapsible section (defined in collapsible-section.ts)
 * - Ctrl+Shift+7 (Cmd+Shift+7 on Mac): Toggle/convert Bullet List
 * - Ctrl+Shift+8 (Cmd+Shift+8 on Mac): Toggle/convert Numbered List
 * - Ctrl+Shift+9 (Cmd+Shift+9 on Mac): Toggle/convert Task List
 * - Tab / Shift+Tab: Increase/decrease indent (list nesting, or paragraph
 *   indent outside lists). Skipped inside tables so cell navigation
 *   (handled by the Table extension) still works.
 * - Enter on an empty list item: ends the list (see list-item-exit.ts).
 *
 * Note: 'Mod' automatically maps to Ctrl on Windows/Linux and Cmd on Mac
 */
export const KeyboardShortcuts = Extension.create({
  name: 'keyboardShortcuts',

  addKeyboardShortcuts() {
    return {
      // Enter on an empty list item ends the list instead of continuing it
      // (Word/Google Docs behavior). Falls through to the normal
      // continue-the-list Enter handling when the cursor isn't on a
      // qualifying empty item.
      Enter: ({ editor }) => exitEmptyListItemOnEnter(editor),

      // Ctrl+S to save current note
      'Mod-s': ({ editor }) => {
        const event = new CustomEvent('saveNoteShortcut');
        editor.view.dom.dispatchEvent(event);
        return true;
      },

      // Ctrl+K to insert/edit a web link
      'Mod-k': ({ editor }) => {
        const event = new CustomEvent('openLinkDialog');
        editor.view.dom.dispatchEvent(event);
        return true;
      },

      // Ctrl+Shift+L to insert a note link
      'Mod-Shift-l': ({ editor }) => {
        // Trigger the note link dialog by calling the component method
        // This will be handled by dispatching a custom event that the component listens for
        const event = new CustomEvent('openNoteLinkDialog');
        editor.view.dom.dispatchEvent(event);
        return true;
      },

      // Ctrl+Shift+T to insert a table
      'Mod-Shift-t': ({ editor }) => {
        return editor
          .chain()
          .focus()
          .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
          .run();
      },

      // Ctrl+Shift+R to insert a row below in table
      'Mod-Shift-r': ({ editor }) => {
        // Only works when inside a table
        if (!editor.isActive('table')) {
          return false;
        }
        return editor
          .chain()
          .focus()
          .addRowAfter()
          .run();
      },

      // Ctrl+Shift+7 to toggle/convert a bullet list
      'Mod-Shift-7': ({ editor }) => toggleBulletListSafely(editor),

      // Ctrl+Shift+8 to toggle/convert a numbered list
      'Mod-Shift-8': ({ editor }) => toggleOrderedListSafely(editor),

      // Ctrl+Shift+9 to toggle/convert a task list
      'Mod-Shift-9': ({ editor }) => toggleTaskListSafely(editor),

      // Tab / Shift+Tab to increase/decrease indent (list nesting, or
      // paragraph indent outside lists). Left unhandled inside tables so
      // the Table extension's own Tab/Shift-Tab cell navigation still works.
      Tab: ({ editor }) => {
        if (editor.isActive('table') || editor.isActive('tableCell') || editor.isActive('tableHeader')) {
          return false;
        }
        return indentAtCursor(editor);
      },

      'Shift-Tab': ({ editor }) => {
        if (editor.isActive('table') || editor.isActive('tableCell') || editor.isActive('tableHeader')) {
          return false;
        }
        return outdentAtCursor(editor);
      },

      // Add more shortcuts here following this pattern:
      // 'Mod-Shift-x': ({ editor }) => {
      //   // Your action here
      //   return true; // Return true if handled, false otherwise
      // },
    };
  },
});
